import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Camera, MapPin, LogIn, LogOut, Clock, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Employee Attendance tab — GPS check-in / selfie / check-out.
 *
 * Endpoints
 *   POST /api/panel/employee/attendance/check-in     { location }
 *   POST /api/panel/employee/attendance/selfie       (multipart file)
 *   POST /api/panel/employee/attendance/check-out    { location }
 *   GET  /api/panel/employee/attendance/today
 *   GET  /api/panel/employee/attendance/history
 */
export default function EmployeeAttendanceTab({ api }) {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const showMsg = (kind, text) => {
    setMsg({ kind, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([
        api('GET', '/employee/attendance/today'),
        api('GET', '/employee/attendance/history?limit=10'),
      ]);
      setToday(t.data?.attendance || null);
      setHistory(h.data?.items || []);
    } catch (e) {
      console.warn('attendance load failed', e?.message);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const getLocation = () => new Promise((resolve) => {
    if (!('geolocation' in navigator)) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { timeout: 8000, enableHighAccuracy: true },
    );
  });

  const handleCheckIn = async () => {
    setBusy(true);
    try {
      const location = await getLocation();
      const res = await api('POST', '/employee/attendance/check-in', { location });
      setToday(res.data?.attendance || null);
      showMsg('ok', res.data?.already_checked_in ? 'Already checked in today' : 'Checked in successfully');
    } catch (e) {
      showMsg('err', e?.response?.data?.detail || 'Check-in failed');
    } finally { setBusy(false); }
  };

  const handleSelfie = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api('POST', '/employee/attendance/selfie', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setToday((t) => t ? { ...t, selfie_url: res.data?.selfie_url } : t);
      showMsg('ok', 'Selfie uploaded');
    } catch (e) {
      showMsg('err', e?.response?.data?.detail || 'Selfie upload failed');
    } finally { setBusy(false); }
  };

  const handleCheckOut = async () => {
    setBusy(true);
    try {
      const location = await getLocation();
      const res = await api('POST', '/employee/attendance/check-out', { location });
      setToday(res.data?.attendance || null);
      showMsg('ok', `Checked out. Total hours: ${res.data?.attendance?.total_hours ?? '—'}`);
      load();
    } catch (e) {
      showMsg('err', e?.response?.data?.detail || 'Check-out failed');
    } finally { setBusy(false); }
  };

  const isIn = !!today && !today.check_out_at;
  const isDone = !!today && !!today.check_out_at;

  return (
    <div className="space-y-5">
      {msg && (
        <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${msg.kind === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.kind === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{msg.text}</span>
        </div>
      )}

      {/* Today's status card */}
      <div className="bg-white rounded-xl border shadow-sm p-5" data-testid="emp-att-today-card">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Today</div>
        {today ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-600">Check-in</div>
                <div className="text-lg font-bold text-slate-900">{new Date(today.check_in_at).toLocaleTimeString()}</div>
              </div>
              {today.check_out_at && (
                <div className="text-right">
                  <div className="text-sm text-slate-600">Check-out</div>
                  <div className="text-lg font-bold text-slate-900">{new Date(today.check_out_at).toLocaleTimeString()}</div>
                </div>
              )}
            </div>
            {today.total_hours != null && (
              <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
                <Clock size={14} /> {today.total_hours} hours worked
              </div>
            )}
            {today.check_in_location && (
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <MapPin size={12} /> {today.check_in_location.lat?.toFixed(5)}, {today.check_in_location.lng?.toFixed(5)} (±{Math.round(today.check_in_location.accuracy || 0)}m)
              </div>
            )}
            {today.selfie_url && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Check-in selfie:</div>
                <img src={today.selfie_url} alt="check-in selfie" className="w-24 h-24 object-cover rounded-lg border" />
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-500">You haven&apos;t checked in yet today.</div>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {!isIn && !isDone && (
          <Button
            onClick={handleCheckIn}
            disabled={busy}
            data-testid="emp-att-check-in"
            className="h-12 bg-green-600 hover:bg-green-700 text-white"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : <><LogIn size={18} className="mr-2" /> Check In</>}
          </Button>
        )}
        {isIn && !today?.selfie_url && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={(e) => handleSelfie(e.target.files?.[0])}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="emp-att-selfie"
              className="h-12 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Camera size={18} className="mr-2" /> Take Selfie
            </Button>
          </>
        )}
        {isIn && (
          <Button
            onClick={handleCheckOut}
            disabled={busy}
            data-testid="emp-att-check-out"
            className="h-12 bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : <><LogOut size={18} className="mr-2" /> Check Out</>}
          </Button>
        )}
        {isDone && (
          <div className="sm:col-span-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm p-3 flex items-center gap-2">
            <CheckCircle2 size={16} /> You&apos;ve completed today&apos;s attendance. See you tomorrow!
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Recent attendance</div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500">No previous attendance records.</div>
        ) : (
          <div className="divide-y">
            {history.map((h) => (
              <div key={h.id} className="py-2 flex justify-between items-center text-sm">
                <div>
                  <div className="font-semibold text-slate-900">{new Date(h.check_in_at).toLocaleDateString()}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(h.check_in_at).toLocaleTimeString()} → {h.check_out_at ? new Date(h.check_out_at).toLocaleTimeString() : '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-slate-900">{h.total_hours != null ? `${h.total_hours}h` : '—'}</div>
                  {h.selfie_url && <a href={h.selfie_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline">View selfie</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
